import { Link as SolidLink, useLocation } from '@solidjs/router';
import { For, Show, createMemo, createSignal, useContext, type JSX } from 'solid-js';
import clsx from 'clsx';
import { ErrorCatcher } from '../../../tools/error-catcher';
import urlMatches from '../../../tools/url-match';
import { PageContext } from '../../page/page-context-provider';
import { ChevronRightIcon } from '../../icons-library/chevron-right';
import { isExternalLink } from '../../../tools/check-is-external-url';
import { isDownloadLink } from '../../../tools/check-is-download-link';
import { removeTrailingSlash } from '../../../tools/remove-trailing-slash';
import removeAmpersand from '../../../tools/remove-ampersand';

export type MenuContent = {
    menu: MenuItem[];
    menuName: string;
};

export type MenuItem = {
    url: string;
    title: string;
    children?: MenuItem[];
};

const formatUrl = (url: string) => removeAmpersand(removeTrailingSlash(url));
const opensInNewTab = (url: string) => isExternalLink(url) || isDownloadLink(url);

const isOnActivePath = (item: MenuItem): boolean => {
    if (urlMatches(item.url)) {
        return true;
    }
    if (!item.children) {
        return false;
    }
    return item.children.some((child) => isOnActivePath(child));
};

/* `sidebar` — original column-bound rendering for the desktop left rail.
   `mobile-panel` — same logic, but bg/border/hover are lifted onto an outer
   bleed wrapper so they extend past the parent Section's grid cell to the
   viewport edges. Row content (icons, text, indent) stays in place. */
export type SidebarMenuVariant = 'sidebar' | 'mobile-panel';

type RowProps = {
    item: MenuItem;
    href: string;
    hasChildren: boolean;
    isExpanded: boolean;
    isActiveLeaf: boolean;
    depth: 1 | 2 | 3;
    variant?: SidebarMenuVariant;
    /* Receives the current expanded state so the handler can flip it without
       re-calling `isOnActivePath` (which uses `useMatch` — only safe during
       render, crashes when called from a click handler). */
    onToggleExpand?: (currentExpanded: boolean) => void;
};

const MenuRow = (props: RowProps) => {
    const variant = (): SidebarMenuVariant => props.variant ?? 'sidebar';

    const leftIndentClass =
        props.depth === 1
            ? (props.isActiveLeaf ? 'pl-cvi-8' : 'pl-cvi-4')
            : props.depth === 2
                ? 'pl-cvi-8'
                : 'pl-cvi-9';

    const rightPaddingClass = 'pr-cvi-4';
    const childPaddingYClass = props.depth === 1 ? 'py-cvi-6' : 'py-cvi-4';

    /* Uniform `label-md` (16px/24) at every depth — sidebar conveys hierarchy via
       indent + active-dot, not via shrinking text. */
    const typographyClass = 'label-md';
    const weightClass = (props.isExpanded || props.isActiveLeaf) ? 'font-cvi-medium' : 'font-cvi-regular';
    const bgClass = props.isExpanded ? 'bg-cvi-bg-block-neutral' : '';
    const borderClass =
        props.isExpanded ? 'border-b border-cvi-border-block-accent'
            : props.depth === 1 ? 'border-b border-cvi-border-subtle'
                : '';
    const radiusClass = props.isActiveLeaf ? 'rounded-cvi-sm' : '';

    /* Bg/border/radius — applied to the row itself in sidebar mode, and
       lifted to a behind-row absolute bg layer in mobile-panel mode so the
       paint extends past the Section grid cell to viewport edges. */
    const rowVisualClass = clsx(bgClass, borderClass, radiusClass);

    const rowClass = clsx(
        'flex items-stretch min-h-cvi-tap-target w-full text-cvi-fg-default',
        variant() === 'sidebar' && rowVisualClass,
        variant() === 'sidebar' && 'hover:bg-cvi-bg-menu-hover transition-colors duration-200',
    );

    const newTabAttrs = opensInNewTab(props.item.url)
        ? { target: '_blank' as const, rel: 'noopener noreferrer' }
        : {};

    const title = () => removeAmpersand(props.item.title);

    const renderChevron = () => (
        <span
            class={clsx(
                'inline-flex transition-transform duration-200 text-cvi-fg-link',
                props.isExpanded && 'rotate-90'
            )}
        >
            <ChevronRightIcon size="sm-md" ariaHidden />
        </span>
    );

    const renderDot = () => (
        <Show when={props.isActiveLeaf}>
            <span
                aria-hidden="true"
                class="absolute -left-cvi-6 top-cvi-4 size-cvi-3 rounded-cvi-round bg-cvi-fg-brand"
            />
        </Show>
    );

    const isExpandedMemo = createMemo(() => props.isExpanded);

    const handleToggleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        props.onToggleExpand?.(isExpandedMemo());
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isExpandedMemo()) {
            e.preventDefault();
            e.stopPropagation();
            props.onToggleExpand?.(true);
        }
    };

    /* In mobile-panel mode, wrap the row with an absolute bg layer that bleeds
       past the parent grid cell to viewport edges. The row itself keeps its
       column width and indent — only the painted bg/border layer extends.
       Hover on any descendant propagates up to `group/row`, so the bg layer
       picks up the menu-hover bg via `group-hover/row:`. Sidebar mode renders
       the row directly. */
    const withBleed = (rowJsx: JSX.Element): JSX.Element =>
        variant() === 'mobile-panel'
            ? (
                <div class="relative isolate group/row">
                    <div
                        aria-hidden="true"
                        class={clsx(
                            'bleed-bg-mobile transition-colors duration-200',
                            rowVisualClass,
                            'group-hover/row:bg-cvi-bg-menu-hover',
                        )}
                    />
                    {rowJsx}
                </div>
            )
            : rowJsx;

    // Case 3: hybrid — has children AND url. Split label and chevron into link + button.
    if (props.hasChildren && props.item.url && props.onToggleExpand) {
        return withBleed(
            <div class={rowClass}>
                <SolidLink
                    href={props.href}
                    class={clsx(
                        'relative flex-1 flex items-center gap-cvi-5 min-w-0 no-underline text-cvi-fg-default',
                        leftIndentClass,
                        childPaddingYClass,
                        typographyClass,
                        weightClass,
                        'focus-ring-cvi-inset'
                    )}
                    {...newTabAttrs}
                >
                    {renderDot()}
                    <span class="flex-1 min-w-0 break-words">{title()}</span>
                </SolidLink>
                <button
                    type="button"
                    class="flex items-center justify-center shrink-0 self-center size-cvi-tap-target bg-transparent border-0 cursor-pointer text-cvi-fg-default focus-ring-cvi-inset"
                    onClick={handleToggleClick}
                    onKeyDown={handleKeyDown}
                    aria-expanded={props.isExpanded}
                    aria-label={props.isExpanded ? `Collapse ${title()}` : `Expand ${title()}`}
                >
                    {renderChevron()}
                </button>
            </div>
        );
    }

    // Case 2: pure parent — children but no url. Whole row toggles.
    if (props.hasChildren && props.onToggleExpand) {
        return withBleed(
            <button
                type="button"
                class={clsx(
                    rowClass,
                    leftIndentClass,
                    rightPaddingClass,
                    childPaddingYClass,
                    'bg-transparent border-0 cursor-pointer text-left focus-ring-cvi-inset'
                )}
                onClick={handleToggleClick}
                onKeyDown={handleKeyDown}
                aria-expanded={props.isExpanded}
            >
                <span class="relative flex flex-1 items-center gap-cvi-5 min-w-0">
                    <span class={clsx('flex-1 min-w-0 break-words', typographyClass, weightClass)}>
                        {title()}
                    </span>
                    <span class="flex items-center justify-center size-cvi-icon-box-sm shrink-0 text-cvi-fg-default">
                        {renderChevron()}
                    </span>
                </span>
            </button>
        );
    }

    // Case 1: leaf — plain link.
    return withBleed(
        <SolidLink
            href={props.href}
            class={clsx(
                rowClass,
                leftIndentClass,
                rightPaddingClass,
                childPaddingYClass,
                'no-underline focus-ring-cvi-inset'
            )}
            {...newTabAttrs}
        >
            <span class="relative flex flex-1 items-center gap-cvi-5 min-w-0">
                {renderDot()}
                <span class={clsx('flex-1 min-w-0 break-words', typographyClass, weightClass)}>
                    {title()}
                </span>
            </span>
        </SolidLink>
    );
};

type SidebarMenuDesktopProps = {
    variant?: SidebarMenuVariant;
};

export const SidebarMenuDesktop = (props: SidebarMenuDesktopProps = {}) => {
    const { sidebarMenu } = useContext(PageContext);
    const location = useLocation();

    // L3 href: support same-page anchor hops ("#section" → current-path + anchor).
    const resolveHref = (url: string) => (url?.includes('#')
        ? formatUrl(location.pathname + url)
        : formatUrl(url));

    const [userExpanded, setUserExpanded] = createSignal<Record<string, boolean>>({});

    const isItemExpanded = (item: MenuItem) => {
        const override = userExpanded()[item.url];
        return override !== undefined ? override : isOnActivePath(item);
    };

    const toggleExpand = (item: MenuItem, currentExpanded: boolean) => {
        setUserExpanded({ ...userExpanded(), [item.url]: !currentExpanded });
    };

    return (
        <ErrorCatcher componentName="Secondary menu">
            <ul class="flex flex-col pb-cvi-11">
                <For each={sidebarMenu()?.menu}>
                    {(l1Item: MenuItem) => {
                        const l1HasChildren = () => Array.isArray(l1Item.children) && l1Item.children.length > 0;
                        /* createMemo so keydown handlers can safely read the expanded state
                           without re-calling `isOnActivePath` (which uses `useMatch`) from
                           event-handler scope where it'd crash. */
                        const l1Expanded = createMemo(() => isItemExpanded(l1Item));
                        /* ESC anywhere in the L1 subtree (incl. child links) collapses L1.
                           Inner L2's keydown calls stopPropagation so this only fires if the
                           innermost handler doesn't consume it. */
                        const handleL1KeyDown = (e: KeyboardEvent) => {
                            if (e.key === 'Escape' && l1Expanded() && l1HasChildren()) {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleExpand(l1Item, true);
                            }
                        };
                        return (
                            <li class="pb-0" onKeyDown={handleL1KeyDown}>
                                <MenuRow
                                    item={l1Item}
                                    href={resolveHref(l1Item.url)}
                                    hasChildren={l1HasChildren()}
                                    isExpanded={l1Expanded() && l1HasChildren()}
                                    isActiveLeaf={!l1HasChildren() && urlMatches(l1Item.url)}
                                    depth={1}
                                    variant={props.variant}
                                    onToggleExpand={l1HasChildren() ? (cur) => toggleExpand(l1Item, cur) : undefined}
                                />
                                <Show when={l1HasChildren() && l1Expanded()}>
                                    <ul class="flex flex-col pb-cvi-6">
                                        <For each={l1Item.children}>
                                            {(l2Item: MenuItem) => {
                                                const l2HasChildren = () => Array.isArray(l2Item.children) && l2Item.children.length > 0;
                                                const l2Expanded = createMemo(() => isItemExpanded(l2Item));
                                                const handleL2KeyDown = (e: KeyboardEvent) => {
                                                    if (e.key === 'Escape' && l2Expanded() && l2HasChildren()) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleExpand(l2Item, true);
                                                    }
                                                };
                                                return (
                                                    <li onKeyDown={handleL2KeyDown}>
                                                        <MenuRow
                                                            item={l2Item}
                                                            href={resolveHref(l2Item.url)}
                                                            hasChildren={l2HasChildren()}
                                                            isExpanded={l2Expanded() && l2HasChildren()}
                                                            isActiveLeaf={!l2HasChildren() && urlMatches(l2Item.url)}
                                                            depth={2}
                                                            variant={props.variant}
                                                            onToggleExpand={l2HasChildren() ? (cur) => toggleExpand(l2Item, cur) : undefined}
                                                        />
                                                        <Show when={l2HasChildren() && l2Expanded()}>
                                                            <ul class="flex flex-col">
                                                                <For each={l2Item.children}>
                                                                    {(l3Item: MenuItem) => (
                                                                        <li>
                                                                            <MenuRow
                                                                                item={l3Item}
                                                                                href={resolveHref(l3Item.url)}
                                                                                hasChildren={false}
                                                                                isExpanded={false}
                                                                                isActiveLeaf={urlMatches(l3Item.url)}
                                                                                depth={3}
                                                                                variant={props.variant}
                                                                            />
                                                                        </li>
                                                                    )}
                                                                </For>
                                                            </ul>
                                                        </Show>
                                                    </li>
                                                );
                                            }}
                                        </For>
                                    </ul>
                                </Show>
                            </li>
                        );
                    }}
                </For>
            </ul>
        </ErrorCatcher>
    );
};
